import React from "react";
import { Observer, useObserver } from "mobx-react";
import {
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  makeStyles,
  Switch as MuiSwitch,
  TextField
} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import { Form } from "@modules/formx-react";
import { ThemeType } from "@theme";
import locale from "@bonliva-crm/locale";
import {
  BookingFinanceEditor_ItemFragment as BookingData,
  LegalPersonType
} from "@graphql";
import { Body, PrimaryButton, RegularButton, Spinner } from "@components";
import { useUserProfile } from "@hooks";
import { useNavigation } from "@navigation";
import { CurrencyInput, CurrencyText, FormxDebugger, Switch } from "@components/formx";
import { Select, Text } from "@components/formx_item_form";
import { FormControls, FormSection } from "@components/forms";
import { BookingLegalPersonTypeSection } from "../shared/BookingLegalPersonTypeSection";
import { InvoiceField } from "../components/InvoicesField";
import { EmployeeCosts } from "./components/EmployeeCosts";
import { SubcontractorCosts } from "./components/SubcontractorCosts";
import { SubcontractorDocs } from "./SubcontractorDocs/SubcontractorDocs";
import { BookingFinanceFormStore } from "./BookingFinanceEditorStore";
import { DocumentUploaderDialog } from "@containers/DocumentUploader/DocumentUploaderDialog";
import { Refresh } from "@material-ui/icons";
import { isBonlivaCareCostCenter } from "@bonliva-crm/shared";

type Props = {
  bookingData: BookingData;
  form: BookingFinanceFormStore;
  invoiceDialog: React.ReactNode;
  openInvoiceDialog: () => void;
  fetchBookingData: () => void;
  onClose: (e: React.SyntheticEvent<any>) => void;
};

export function BookingFinanceForm(props: Props) {
  const { bookingData, form, fetchBookingData, onClose } = props;
  const classes = useStyles({});
  const { can } = useUserProfile();

  const snapshot = bookingData.ConsultantSnapshot;
  const isContractTypeEnabled = form?.LegalPersonType === LegalPersonType.Employee;
  const nav = useNavigation();
  const [isGroupInvoiceEditable, setIsGroupInvoiceEditable] = React.useState(false);

  return useObserver(() => {
    nav.lockNavigation("booking_finance_form", form.dirty);
    return (
      <Form store={form} allowUpdates>
        <form
          onSubmit={form.submit}
          onReset={form.onResetHandler}
          data-test="BookingFinanceForm"
        >
          <FormxDebugger />

          <div className={classes.root}>
            <Grid item xs={12} style={{ display: "flex" }}>
              <FormSection flat fullWidth withBorder>
                <BookingLegalPersonTypeSection
                  legalPersonTypeName="LegalPersonType"
                  businessName="BusinessName"
                  businessSSNName="BusinessSocialSecurityNo"
                  updateConsultantName="UpdateConsultantWithSnapshotData"
                  changeMessage={locale.ConsultantCurrentFinanceInformation(
                    locale.legalPersonType[snapshot.LegalPersonType],
                    snapshot.BusinessName,
                    snapshot.BusinessSocialSecurityNo
                  )}
                  legalPersonTypeDisabled
                />

                {isContractTypeEnabled && (
                  <Select
                    disabled={!isContractTypeEnabled || form.isNationalProcurement}
                    name="ContractType"
                    label={locale.ContractType}
                    options={form.contractTypes.toLabelValue(
                      (x) => locale.contractType[x]
                    )}
                  />
                )}

                {form.values.LegalPersonType === LegalPersonType.Subcontractor && (
                  <Switch
                    name="IsConsultantPayed"
                    disabled={!can("booking-finance:update:all")}
                    label={locale.SubContractorInvoicePayed}
                  />
                )}

                {form.values.LegalPersonType === LegalPersonType.Employee && (
                  <Switch
                    name="IsConsultantPayed"
                    label={locale.IsEmployeePayed}
                    disabled={
                      form.values.IsSickPayed || !can("booking-finance:update:all")
                    }
                  />
                )}

                {form.values.IsConsultantPayed && (
                  <IconButton
                    onClick={() => form.fetchDraftCosts(true, false)}
                    size="small"
                    style={{ marginLeft: 4 }}
                    disabled={form.isDraftCostsLoading}
                  >
                    {form.isDraftCostsLoading ? (
                      <Spinner color="inherit" size={12} />
                    ) : (
                      <Refresh />
                    )}
                  </IconButton>
                )}

                {form.values.LegalPersonType === LegalPersonType.Employee &&
                  form.values.IsConsultantPayed &&
                  !form.isDraftCostsLoading && <EmployeeCosts form={form} />}

                {form.values.LegalPersonType === LegalPersonType.Subcontractor &&
                  form.values.IsConsultantPayed &&
                  !form.isDraftCostsLoading && <SubcontractorCosts form={form} />}

                {form.isDraftCostsLoading && (
                  <>
                    <Grid item>
                      <Skeleton variant="rect" height={40} style={{ marginTop: 8 }} />
                    </Grid>
                    <Grid item>
                      <Skeleton variant="rect" height={40} style={{ marginTop: 8 }} />
                    </Grid>
                    <Grid item>
                      <Skeleton variant="rect" height={40} style={{ marginTop: 8 }} />
                    </Grid>
                  </>
                )}
              </FormSection>
            </Grid>

            {form.values.LegalPersonType === LegalPersonType.Employee &&
              form.values.TimeReportHasSickTime && (
                <Grid item xs={12} style={{ display: "flex" }}>
                  <FormSection flat fullWidth withBorder>
                    <Switch name="IsSickPayed" label={locale.SickPayed} />

                    {form.values.IsSickPayed && (
                      <IconButton
                        onClick={() => form.fetchDraftCosts(false, true)}
                        size="small"
                        style={{ marginLeft: 4 }}
                        disabled={form.isDraftCostsLoading}
                      >
                        {form.isDraftCostsLoading ? (
                          <Spinner color="inherit" size={12} />
                        ) : (
                          <Refresh />
                        )}
                      </IconButton>
                    )}

                    {form.isDraftCostsLoading && (
                      <Skeleton variant="rect" height={40} style={{ marginTop: 8 }} />
                    )}

                    {!form.isDraftCostsLoading && form.values.IsSickPayed && (
                      <CurrencyText
                        name="SickPayTotalStr"
                        label={locale.bookingFinance.sickPay}
                        TextFieldProps={{
                          fullWidth: true
                        }}
                      />
                    )}
                  </FormSection>
                </Grid>
              )}

            {form.values.LegalPersonType === LegalPersonType.Subcontractor && (
              <SubcontractorDocs
                disabled={form.disabled}
                canViewDocuments={form.canReadSubcontractorDocuments}
                canDeleteDocuments={form.canDeleteSubcontractorDocuments}
                canUpload={form.canUploadSubcontractorDocuments}
                onStartUpload={form.openSubcontractorDocumentUpload}
                onRemove={form.removeSubcontractorDocument}
                docs={form.subcontractorDocuments}
              />
            )}

            <Grid item xs={12}>
              <FormSection flat withBorder fullWidth style={{ position: "relative" }}>
                <Body>{locale.CustomerInvoice}</Body>

                <InvoiceField
                  name="Invoices"
                  costCenterId={form.bookingData?.CostCenter?.Id}
                  canAdd={!form.disabled && can("booking-finance:update:all")}
                  canView={can("invoice:read:all")}
                  canResync={can("invoice:write:all")}
                  canDelete={!form.disabled && can("booking-finance:update:all")}
                  onAdd={(e: any) => {
                    if (e.stoppedHack) {
                      return;
                    }
                    props.openInvoiceDialog();
                  }}
                />

                {bookingData.GroupInvoicing && (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <FormControlLabel
                      disabled
                      label={locale.GroupInvoicing}
                      control={<Checkbox disabled checked />}
                    />

                    <MuiSwitch
                      size="small"
                      checked={isGroupInvoiceEditable}
                      onChange={(e) => setIsGroupInvoiceEditable(e.target.checked)}
                    />

                    <CurrencyInput name="GroupInvoiceTotalStr" readonly={!isGroupInvoiceEditable} />
                  </div>
                )}
              </FormSection>
            </Grid>

            <Grid item xs={12}>
              <FormSection flat withBorder fullWidth style={{ position: "relative" }}>
                <Text
                  name="CustomerFine"
                  label={locale.CustomerFine}
                  TextFieldProps={{
                    type: "number"
                  }}
                />
              </FormSection>
            </Grid>

            <Grid item xs={12}>
              <FormSection flat withBorder fullWidth style={{ position: "relative" }}>
                <Text
                  name="AdministrativeFee"
                  disabled
                  label={locale.AdministrativeFee}
                  TextFieldProps={{
                    type: "number"
                  }}
                />
              </FormSection>
            </Grid>

            <Grid item xs={12}>
              <FormSection flat withBorder fullWidth>
                <Text
                  name="Note"
                  label={locale.Notes}
                  TextFieldProps={{
                    multiline: true
                  }}
                  helperText={locale["Press Enter for a new line"]}
                />
              </FormSection>
            </Grid>

            {bookingData.CostCenter &&
              isBonlivaCareCostCenter(bookingData.CostCenter.Id) && (
                <Grid item xs={12}>
                  <FormSection flat withBorder fullWidth>
                    <TextField
                      fullWidth
                      name="PatientId"
                      label={locale.PatientId}
                      value={form.values.PatientId}
                      onChange={({ currentTarget }) => {
                        form.setValue(currentTarget.name, currentTarget.value);
                      }}
                    />
                  </FormSection>
                </Grid>
              )}

            <Grid item xs={12}>
              <FormSection flat withBorder fullWidth>
                <Switch name="AdditionalWork" label={locale.AdditionalWork} />

                <Text
                  name="AdditionalWorkNote"
                  label={locale.AdditionalWorkNotes}
                  disabled={!form.values.AdditionalWork}
                  TextFieldProps={{
                    multiline: true
                  }}
                  helperText={locale["Press Enter for a new line"]}
                />
              </FormSection>
            </Grid>

            <Grid item xs={12}>
              <Observer>
                {() => (
                  <FormControls
                    right={
                      <>
                        <IconButton onClick={() => fetchBookingData()} size="small">
                          <Refresh />
                        </IconButton>
                        <RegularButton onClick={onClose}>{locale.Cancel}</RegularButton>
                        <PrimaryButton
                          type="submit"
                          disabled={
                            !form.customDirty || !form.valid || form.isDraftCostsLoading
                          }
                          loading={form.isSubmitting}
                        >
                          {locale.Save}
                        </PrimaryButton>
                      </>
                    }
                  />
                )}
              </Observer>
            </Grid>
          </div>
        </form>
        <DocumentUploaderDialog
          files={form.subcontractorDocsToUpload}
          onClose={() => (form.subcontractorDocsToUpload = [])}
          onUpload={form.uploadSubcontractorDocuments}
          canSubmit
        />
        {props.invoiceDialog}
      </Form>
    );
  });
}

const useStyles = makeStyles((theme: ThemeType) => ({
  root: {
    padding: 16,

    "& > *:not(:last-child)": {
      marginBottom: 16
    }
  },
  withFileUpload: {
    // borderStyle: "dashed",
    display: "flex",
    flex: 1,
    flexWrap: "wrap",
    justifyContent: "space-between",
    padding: 16,
    paddingTop: 0,
    paddingBottom: 0,

    "& hr": {
      flex: "1 0 100%",
      margin: "0 -16px 16px -16px"
    }
  },

  uploadBtnWrapper: {
    padding: "4px 0",
    flex: "1 0 100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },

  documentList: { flex: "1 0 100%" },
  dndOverlay: { borderRadius: 4 },
  droppable: { borderStyle: "dashed" }
}));
